import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { ImageCropperComponent, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { ModalLayoutModule } from '../modal-layout';

/**
 * Standalone modal that wraps ngx-image-cropper with configurable output size.
 *
 * Opened via `Modal.show({ component: ImageCropperModal, props: { imageBase64 } })`.
 * Dismisses with the cropped image as a Blob, or null if cancelled.
 */
@Component({
    standalone: true,
    imports: [CommonModule, IonicModule, ModalLayoutModule, ImageCropperComponent],
    selector: 'abs-image-cropper-modal',
    template: `
        <abs-modal-layout title="Crop Image" [showClose]="false" [backdropDismiss]="false">
            <div class="cropInfo">
                <div class="cropHint">Drag the image to reposition. Drag the crop box edges to resize.</div>

                <div class="zoomControls">
                    <span class="zoomLabel">Zoom</span>
                    <ion-range [min]="minZoom" max="3" step="0.05" [value]="zoom" (ionInput)="onZoomChange($event)"></ion-range>
                    <span class="zoomValue">{{ zoom | number: '1.0-2' }}×</span>
                </div>

                <div class="cropSize">Output: {{ cropWidth }} × {{ cropHeight }} px</div>
            </div>

            <div
                #cropperWrap
                class="cropperWrap"
                [class.dragging]="isDragging"
                (mousedown)="onDragStart($event)"
                (mousemove)="onDragMove($event)"
                (mouseup)="onDragEnd()"
                (mouseleave)="onDragEnd()"
                (touchstart)="onDragStart($event)"
                (touchmove)="onDragMove($event)"
                (touchend)="onDragEnd()"
                (touchcancel)="onDragEnd()"
                (wheel)="onWheel($event)"
                (scroll)="onScroll()"
            >
                <image-cropper
                    #cropper
                    [imageBase64]="imageBase64"
                    [imageURL]="imageURL"
                    [imageFile]="imageFile"
                    [maintainAspectRatio]="maintainAspectRatio"
                    [aspectRatio]="aspectRatio"
                    [cropperStaticWidth]="cropperFrameWidth"
                    [cropperStaticHeight]="cropperFrameHeight"
                    [resizeToWidth]="cropWidth"
                    [resizeToHeight]="cropHeight"
                    [onlyScaleDown]="false"
                    [format]="exportFormat"
                    output="blob"
                    [autoCrop]="true"
                    [allowMoveImage]="false"
                    [hideResizeSquares]="true"
                    [transform]="transform"
                    (imageCropped)="onCropped($event)"
                    (cropperReady)="onCropperReady()"
                    (loadImageFailed)="onLoadFailed()"
                ></image-cropper>

                <ng-container *ngIf="cropBoxRect">
                    <!-- Corner handles -->
                    <div
                        class="rHandle rHandle-nw"
                        [style.left.px]="cropBoxRect.x - 7"
                        [style.top.px]="cropBoxRect.y - 7"
                        (mousedown)="onResizeStart($event, 'nw')"
                        (touchstart)="onResizeStart($event, 'nw')"
                    ></div>
                    <div
                        class="rHandle rHandle-ne"
                        [style.left.px]="cropBoxRect.x + cropBoxRect.width - 7"
                        [style.top.px]="cropBoxRect.y - 7"
                        (mousedown)="onResizeStart($event, 'ne')"
                        (touchstart)="onResizeStart($event, 'ne')"
                    ></div>
                    <div
                        class="rHandle rHandle-sw"
                        [style.left.px]="cropBoxRect.x - 7"
                        [style.top.px]="cropBoxRect.y + cropBoxRect.height - 7"
                        (mousedown)="onResizeStart($event, 'sw')"
                        (touchstart)="onResizeStart($event, 'sw')"
                    ></div>
                    <div
                        class="rHandle rHandle-se"
                        [style.left.px]="cropBoxRect.x + cropBoxRect.width - 7"
                        [style.top.px]="cropBoxRect.y + cropBoxRect.height - 7"
                        (mousedown)="onResizeStart($event, 'se')"
                        (touchstart)="onResizeStart($event, 'se')"
                    ></div>
                    <!-- Edge bars -->
                    <div
                        class="rBar rBar-n"
                        [style.left.px]="cropBoxRect.x + 14"
                        [style.top.px]="cropBoxRect.y - 4"
                        [style.width.px]="cropBoxRect.width - 28"
                        (mousedown)="onResizeStart($event, 'n')"
                        (touchstart)="onResizeStart($event, 'n')"
                    ></div>
                    <div
                        class="rBar rBar-s"
                        [style.left.px]="cropBoxRect.x + 14"
                        [style.top.px]="cropBoxRect.y + cropBoxRect.height - 4"
                        [style.width.px]="cropBoxRect.width - 28"
                        (mousedown)="onResizeStart($event, 's')"
                        (touchstart)="onResizeStart($event, 's')"
                    ></div>
                    <div
                        class="rBar rBar-w"
                        [style.left.px]="cropBoxRect.x - 4"
                        [style.top.px]="cropBoxRect.y + 14"
                        [style.height.px]="cropBoxRect.height - 28"
                        (mousedown)="onResizeStart($event, 'w')"
                        (touchstart)="onResizeStart($event, 'w')"
                    ></div>
                    <div
                        class="rBar rBar-e"
                        [style.left.px]="cropBoxRect.x + cropBoxRect.width - 4"
                        [style.top.px]="cropBoxRect.y + 14"
                        [style.height.px]="cropBoxRect.height - 28"
                        (mousedown)="onResizeStart($event, 'e')"
                        (touchstart)="onResizeStart($event, 'e')"
                    ></div>
                </ng-container>
            </div>

            <div class="actions">
                <ion-button fill="outline" color="medium" (click)="cancel()">Cancel</ion-button>
                <ion-button (click)="confirm()" [disabled]="!croppedBlob">Confirm</ion-button>
            </div>
        </abs-modal-layout>
    `,
    styles: [
        `
            .cropperWrap {
                position: relative;
                width: 100%;
                max-height: 60vh;
                overflow: auto;
                cursor: grab;
                user-select: none;
            }

            .cropperWrap.dragging {
                cursor: grabbing;
            }

            .cropInfo {
                display: flex;
                flex-direction: column;
                gap: 12px;
                margin-bottom: 12px;
            }

            .cropHint {
                font-size: 0.9rem;
                color: var(--ion-color-medium-shade);
            }

            .zoomControls {
                display: grid;
                grid-template-columns: auto minmax(180px, 1fr) auto;
                gap: 12px;
                align-items: center;
            }

            .zoomLabel,
            .zoomValue {
                font-size: 0.9rem;
                white-space: nowrap;
            }

            .zoomValue {
                text-align: right;
                min-width: 48px;
            }

            .cropSize {
                text-align: center;
                font-size: 0.9rem;
                color: var(--ion-color-medium-shade);
                padding: 8px;
                background: var(--ion-color-light-shade);
                border-radius: 4px;
            }

            .actions {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
                padding: 12px 0 0;
            }

            @media (max-width: 640px) {
                .zoomControls {
                    grid-template-columns: 1fr;
                }

                .zoomValue {
                    text-align: left;
                }
            }

            .rHandle {
                position: absolute;
                width: 14px;
                height: 14px;
                background: rgba(0, 123, 255, 0.9);
                border: 2px solid white;
                border-radius: 2px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
                z-index: 10;
                pointer-events: all;
            }

            .rHandle-nw {
                cursor: nwse-resize;
            }
            .rHandle-ne {
                cursor: nesw-resize;
            }
            .rHandle-sw {
                cursor: nesw-resize;
            }
            .rHandle-se {
                cursor: nwse-resize;
            }

            .rBar {
                position: absolute;
                background: rgba(0, 123, 255, 0.55);
                border: 1px solid rgba(255, 255, 255, 0.7);
                border-radius: 3px;
                z-index: 10;
                pointer-events: all;
            }

            .rBar-n,
            .rBar-s {
                height: 8px;
                cursor: ns-resize;
            }
            .rBar-e,
            .rBar-w {
                width: 8px;
                cursor: ew-resize;
            }

            /* Prevent ngx-image-cropper from moving the crop box on drag */
            ::ng-deep image-cropper .ngx-ic-move {
                pointer-events: none !important;
                cursor: inherit !important;
            }
        `
    ]
})
export class ImageCropperModal implements OnInit {
    /** Passed as componentProp by the caller */
    imageBase64?: string;
    imageURL?: string;
    imageFile?: File;
    cropWidth = 512;
    cropHeight = 512;
    initialCropWidth?: number;
    initialCropHeight?: number;
    /** When true, the caller's cropWidth/cropHeight are the output contract and must
     * never be overwritten by anything derived from the crop box — see the class doc
     * for why this exists. */
    @Input() fixedOutputSize = false;
    /** 'png' is the existing default (every current caller expects it) — set to 'jpeg'
     * or 'webp' for photographic content, where PNG's lossless encoding produces a
     * needlessly large file for no visual benefit. Keep PNG wherever transparency
     * genuinely matters (e.g. a unit token with a transparent background). */
    @Input() exportFormat: 'png' | 'jpeg' | 'webp' = 'png';
    /** When false the user can drag a free-form crop box of any aspect ratio. Defaults to false for adjustable crop dimensions. */
    maintainAspectRatio = false;
    minZoom = 1;
    initialZoom = 1;
    initialTransform: ImageTransform = { scale: 1, translateH: 0, translateV: 0, translateUnit: 'px' };

    croppedBlob: Blob | null = null;
    zoom = 1;
    transform: ImageTransform = { scale: 1, translateH: 0, translateV: 0, translateUnit: 'px' };

    isDragging = false;
    private lastDragPos: { x: number; y: number } | null = null;
    private isPinching = false;
    private pinchStartDist = 0;
    private pinchStartZoom = 1;

    private static pinchDist(touches: TouchList): number {
        const dx = touches[1].clientX - touches[0].clientX;
        const dy = touches[1].clientY - touches[0].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    @ViewChild('cropper') cropperRef: ImageCropperComponent;
    @ViewChild('cropper', { read: ElementRef }) cropperElRef: ElementRef;
    @ViewChild('cropperWrap') cropperWrapRef: ElementRef;

    cropBoxRect: { x: number; y: number; width: number; height: number } | null = null;

    constructor(
        private modalController: ModalController,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        // componentProps are set before ngOnInit, so initialZoom/initialTransform are available here
        this.zoom = this.initialZoom;
        this.transform = { ...this.initialTransform };
        if (this.initialCropWidth) this.cropWidth = this.initialCropWidth;
        if (this.initialCropHeight) this.cropHeight = this.initialCropHeight;
        // Scale crop box to fit on screen without coupling width/height axes
        const scale = Math.min(1, 360 / Math.max(this.cropWidth, this.cropHeight));
        this.cropBoxDisplayWidth = Math.round(this.cropWidth * scale);
        this.cropBoxDisplayHeight = Math.round(this.cropHeight * scale);
    }

    get aspectRatio(): number {
        return this.cropWidth / this.cropHeight;
    }

    // On-screen size of the crop box — independent of the output image dimensions
    cropBoxDisplayWidth = 360;
    cropBoxDisplayHeight = 360;

    get cropperFrameWidth(): number {
        return this.cropBoxDisplayWidth;
    }
    get cropperFrameHeight(): number {
        return this.cropBoxDisplayHeight;
    }

    onCropped(event: ImageCroppedEvent) {
        this.croppedBlob = event.blob ?? null;
        this.updateCropBoxRect();
    }

    /** Extract the actual crop box size from the cropper's internal data and use as output dimensions. */
    extractCropBoxDimensions() {
        if (this.fixedOutputSize) {
            return;
        }
        if (!this.cropperRef) return;

        try {
            const cropper = this.cropperRef as any;
            let newWidth = 0;
            let newHeight = 0;

            // ngx-image-cropper stores crop data in the cropData property
            // which contains { x, y, width, height } of the crop area in image coordinates
            if (cropper.cropData && typeof cropper.cropData.width === 'number' && typeof cropper.cropData.height === 'number') {
                newWidth = Math.round(cropper.cropData.width);
                newHeight = Math.round(cropper.cropData.height);
            }

            // Fallback: Try imageData
            if (
                (newWidth <= 0 || newHeight <= 0) &&
                cropper.imageData &&
                typeof cropper.imageData.width === 'number' &&
                typeof cropper.imageData.height === 'number'
            ) {
                newWidth = Math.round(cropper.imageData.width);
                newHeight = Math.round(cropper.imageData.height);
            }

            // Only update if dimensions changed to avoid infinite loops
            if (newWidth > 0 && newHeight > 0 && (newWidth !== this.cropWidth || newHeight !== this.cropHeight)) {
                this.cropWidth = newWidth;
                this.cropHeight = newHeight;
            }
        } catch (e) {
            // Silently ignore errors — dimensions will remain as-is
        }
    }

    onCropperReady() {
        this.extractCropBoxDimensions();
        this.updateCropBoxRect();
    }

    private updateCropBoxRect() {
        setTimeout(() => {
            try {
                const cropperEl = this.cropperElRef?.nativeElement;
                const wrapEl = this.cropperWrapRef?.nativeElement;
                if (!cropperEl || !wrapEl) return;

                const overlayEl = cropperEl.querySelector('.ngx-ic-cropper') ?? cropperEl.querySelector('.ngx-ic-overlay');
                if (!overlayEl) return;

                const overlayRect = (overlayEl as HTMLElement).getBoundingClientRect();
                const wrapRect = (wrapEl as HTMLElement).getBoundingClientRect();

                // Account for scroll offset within the wrapper
                const scrollLeft = wrapEl.scrollLeft;
                const scrollTop = wrapEl.scrollTop;

                this.cropBoxRect = {
                    x: overlayRect.left - wrapRect.left + scrollLeft,
                    y: overlayRect.top - wrapRect.top + scrollTop,
                    width: overlayRect.width,
                    height: overlayRect.height
                };
                this.cdr.detectChanges();
            } catch (e) {
                // ignore
            }
        }, 0);
    }

    onZoomChange(event: Event) {
        const value = (event as CustomEvent<{ value: number }>).detail?.value;
        if (typeof value === 'number') this.setZoom(value);
    }

    onWheel(event: WheelEvent) {
        // ctrlKey is set by the browser for trackpad pinch gestures
        if (!event.ctrlKey) return;
        event.preventDefault();
        const delta = -event.deltaY * 0.008;
        this.setZoom(this.zoom + delta);
    }

    onDragStart(event: MouseEvent | TouchEvent) {
        if ('touches' in event && event.touches.length === 2) {
            // Two fingers — begin pinch
            this.isPinching = true;
            this.isDragging = false;
            this.lastDragPos = null;
            this.pinchStartDist = ImageCropperModal.pinchDist(event.touches);
            this.pinchStartZoom = this.zoom;
            event.preventDefault();
            return;
        }
        this.isDragging = true;
        this.isPinching = false;
        const pos = 'touches' in event ? event.touches[0] : event;
        this.lastDragPos = { x: pos.clientX, y: pos.clientY };
        event.preventDefault();
    }

    onDragMove(event: MouseEvent | TouchEvent) {
        if ('touches' in event && event.touches.length === 2) {
            if (!this.isPinching) return;
            event.preventDefault();
            const dist = ImageCropperModal.pinchDist(event.touches);
            this.setZoom(this.pinchStartZoom * (dist / this.pinchStartDist));
            return;
        }
        if (!this.isDragging || !this.lastDragPos) return;
        const pos = 'touches' in event ? event.touches[0] : event;
        const dx = (pos.clientX - this.lastDragPos.x) / this.zoom;
        const dy = (pos.clientY - this.lastDragPos.y) / this.zoom;
        this.lastDragPos = { x: pos.clientX, y: pos.clientY };
        this.transform = {
            ...this.transform,
            translateH: (this.transform.translateH ?? 0) + dx,
            translateV: (this.transform.translateV ?? 0) + dy,
            translateUnit: 'px'
        };
    }

    onDragEnd() {
        this.isDragging = false;
        this.isPinching = false;
        this.lastDragPos = null;
    }

    onScroll() {
        // Update crop box rect when the wrapper scrolls so handles stay aligned
        this.updateCropBoxRect();
    }

    private resizeHandle: string | null = null;
    private resizeStartData: {
        x: number;
        y: number;
        cropWidth: number;
        cropHeight: number;
        displayWidth: number;
        displayHeight: number;
        centerX: number;
        centerY: number;
    } | null = null;

    onResizeStart(event: MouseEvent | TouchEvent, handle: string) {
        event.preventDefault();
        event.stopPropagation();

        const pos = 'touches' in event ? event.touches[0] : (event as MouseEvent);
        this.resizeHandle = handle;

        this.resizeStartData = {
            x: pos.clientX,
            y: pos.clientY,
            cropWidth: this.cropWidth,
            cropHeight: this.cropHeight,
            displayWidth: this.cropBoxDisplayWidth,
            displayHeight: this.cropBoxDisplayHeight,
            centerX: this.cropBoxRect ? this.cropBoxRect.x + this.cropBoxRect.width / 2 : 0,
            centerY: this.cropBoxRect ? this.cropBoxRect.y + this.cropBoxRect.height / 2 : 0
        };

        const onResizeMove = (moveEvent: MouseEvent | TouchEvent) => this.onResizeMove(moveEvent);
        const onResizeEnd = () => {
            document.removeEventListener('mousemove', onResizeMove as any);
            document.removeEventListener('touchmove', onResizeMove as any);
            document.removeEventListener('mouseup', onResizeEnd);
            document.removeEventListener('touchend', onResizeEnd);
            this.resizeHandle = null;
            this.resizeStartData = null;
        };

        document.addEventListener('mousemove', onResizeMove as any);
        document.addEventListener('touchmove', onResizeMove as any);
        document.addEventListener('mouseup', onResizeEnd);
        document.addEventListener('touchend', onResizeEnd);
    }

    private onResizeMove(event: MouseEvent | TouchEvent) {
        if (!this.resizeHandle || !this.resizeStartData) return;

        const pos = 'touches' in event ? event.touches[0] : (event as MouseEvent);
        const dx = pos.clientX - this.resizeStartData.x;
        const dy = pos.clientY - this.resizeStartData.y;

        const handle = this.resizeHandle;
        const minDisplay = 50;
        let newDisplayW = this.resizeStartData.displayWidth;
        let newDisplayH = this.resizeStartData.displayHeight;

        // Each axis is fully independent — dragging one never affects the other
        if (handle === 'e' || handle === 'ne' || handle === 'se')
            newDisplayW = Math.max(minDisplay, this.resizeStartData.displayWidth + dx);
        if (handle === 'w' || handle === 'nw' || handle === 'sw')
            newDisplayW = Math.max(minDisplay, this.resizeStartData.displayWidth - dx);
        if (handle === 's' || handle === 'se' || handle === 'sw')
            newDisplayH = Math.max(minDisplay, this.resizeStartData.displayHeight + dy);
        if (handle === 'n' || handle === 'ne' || handle === 'nw')
            newDisplayH = Math.max(minDisplay, this.resizeStartData.displayHeight - dy);

        this.cropBoxDisplayWidth = Math.round(newDisplayW);
        this.cropBoxDisplayHeight = Math.round(newDisplayH);

        if (!this.fixedOutputSize) {
            // Output dimensions scale proportionally to the display size change
            const imgScaleX = this.resizeStartData.cropWidth / this.resizeStartData.displayWidth;
            const imgScaleY = this.resizeStartData.cropHeight / this.resizeStartData.displayHeight;
            const newCropWidth = Math.round(newDisplayW * imgScaleX);
            const newCropHeight = Math.round(newDisplayH * imgScaleY);
            // If dimensions changed, invalidate the blob so it gets regenerated at the new size
            if (newCropWidth !== this.cropWidth || newCropHeight !== this.cropHeight) {
                this.croppedBlob = null;
            }
            this.cropWidth = newCropWidth;
            this.cropHeight = newCropHeight;
        } else {
            // Output size is fixed; the display box changing size still means the
            // selected region changed, so the cached blob is stale either way.
            this.croppedBlob = null;
        }

        // Update handle positions immediately — crop box expands from its center
        this.cropBoxRect = {
            x: this.resizeStartData.centerX - this.cropBoxDisplayWidth / 2,
            y: this.resizeStartData.centerY - this.cropBoxDisplayHeight / 2,
            width: this.cropBoxDisplayWidth,
            height: this.cropBoxDisplayHeight
        };

        this.cdr.detectChanges();
        this.triggerRecrop();
    }

    private triggerRecrop() {
        if (!this.cropperRef) return;
        try {
            const cropper = this.cropperRef as any;
            if (typeof cropper.crop === 'function') {
                cropper.crop();
            }
        } catch (e) {
            // ignore
        }
    }

    private setZoom(value: number) {
        this.zoom = Math.min(3, Math.max(this.minZoom, value));
        this.transform = { ...this.transform, scale: this.zoom };
    }

    onLoadFailed() {
        this.croppedBlob = null;
    }

    async confirm() {
        if (!this.croppedBlob) return;

        // Always redraw to a canvas at the exact output dimensions — the blob from
        // ngx-image-cropper may be at display or default dimensions, not cropWidth×cropHeight.
        const finalBlob = await this.resizeBlobToOutputDimensions(this.croppedBlob);

        // Extract crop coordinates in image space from the cropper's internal state
        let cropX = 0;
        let cropY = 0;
        if (this.cropperRef) {
            try {
                const cropper = this.cropperRef as any;
                if (cropper.cropData) {
                    cropX = Math.round(cropper.cropData.x || 0);
                    cropY = Math.round(cropper.cropData.y || 0);
                }
            } catch (e) {
                // If we can't extract coords, just use defaults
            }
        }

        await this.modalController.dismiss({
            blob: finalBlob,
            zoom: this.zoom,
            transform: this.transform,
            cropWidth: this.cropWidth,
            cropHeight: this.cropHeight,
            cropX,
            cropY
        });
    }

    /** Draw blob onto a canvas of exactly cropWidth × cropHeight. */
    private resizeBlobToOutputDimensions(blob: Blob): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = this.cropWidth;
                    canvas.height = this.cropHeight;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('No canvas context'));
                        return;
                    }
                    ctx.drawImage(img, 0, 0, this.cropWidth, this.cropHeight);
                    this.canvasToBlob(canvas).then(resolve).catch(reject);
                };
                img.onerror = () => reject(new Error('Failed to load blob as image'));
                img.src = reader.result as string;
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
        });
    }

    async cancel() {
        await this.modalController.dismiss(null);
    }

    /**
     * Convert a canvas element to a Blob.
     */
    private canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const mimeType = this.exportFormat === 'png' ? 'image/png' : `image/${this.exportFormat}`;
            const quality = this.exportFormat === 'png' ? undefined : 0.85;
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to convert canvas to blob'));
                    }
                },
                mimeType,
                quality
            );
        });
    }
}
